import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(isInstalled);

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Listen for install prompt (Android / Chrome / Edge / desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show install prompt after 3 seconds
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS Safari never fires beforeinstallprompt — show manual "Add to Home Screen" tip
    let iosTimer;
    if (ios && !isInstalled) {
      iosTimer = setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('✅ PWA installed');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone) return null;

  // iOS Install Instructions
  if (isIOS && showPrompt) {
    return (
      <div data-testid="install-prompt-ios" className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border-2 border-[#84CC16] p-4 z-50 animate-in slide-in-from-bottom duration-300">
        <button
          onClick={handleDismiss}
          data-testid="install-dismiss-btn"
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#064E3B] to-[#84CC16] flex items-center justify-center flex-shrink-0">
            <Download className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white mb-1">Install MoneyFeed</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Install our app for quick access and offline support!
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Tap <span className="inline-flex items-center mx-1">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z"/>
                </svg>
              </span> then "Add to Home Screen"
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop Install Prompt
  if (showPrompt && deferredPrompt) {
    return (
      <div data-testid="install-prompt" className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border-2 border-[#84CC16] p-4 z-50 animate-in slide-in-from-bottom duration-300">
        <button
          onClick={handleDismiss}
          data-testid="install-dismiss-btn"
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex items-start gap-3 mb-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#064E3B] to-[#84CC16] flex items-center justify-center flex-shrink-0">
            <Download className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white mb-1">Install MoneyFeed</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Install our app for quick access, offline support, and push notifications!
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={handleInstallClick}
            data-testid="install-app-btn"
            className="flex-1 bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold"
          >
            <Download className="h-4 w-4 mr-2" />
            Install
          </Button>
          <Button
            onClick={handleDismiss}
            variant="outline"
            className="dark:bg-gray-700 dark:text-white"
          >
            Later
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;
